import { CREATE_REQUEST_STATUS, REQUEST_TYPES } from "@demandproai/shared";

const ACTIVE_STATUSES = [CREATE_REQUEST_STATUS.PENDING, CREATE_REQUEST_STATUS.DEFERRED, CREATE_REQUEST_STATUS.IN_PROGRESS];

/**
 * useResultProgress
 *
 * @param {Object} createRequests - caseData.createRequests
 * @param {boolean} isDraftCaseData - caseData.isDraftCase
 * @returns {{
 *   isDemandSuccessful: boolean,
 *   isDemandFailed: boolean,
 *   isDemandProcessing: boolean,
 *   isDemandDraft: boolean,
 *   isVirtualUserRunning: boolean,
 * }}
 */
const useResultProgress = (createRequests, isDraftCaseData) => {
  const demandRequest = createRequests?.[REQUEST_TYPES.DEMAND] ?? null;
  const virtualUserRequest = createRequests?.[REQUEST_TYPES.VIRTUAL_USER] ?? null;

  const demandStatus = demandRequest?.status ?? null;
  const virtualUserStatus = virtualUserRequest?.status ?? null;

  // A restart runs the assistant again, so a virtual-user request created after the last demand supersedes
  // it. Until then its status masks the run: a just-superseded demand would read as FAILED for the whole
  // length of the new run. When the run is the newer one, its status is what the case is actually doing.
  const isVirtualUserNewerThanDemand = Boolean(virtualUserRequest) && (
    !demandRequest || new Date(virtualUserRequest.createdAt) > new Date(demandRequest.createdAt)
  );
  const effectiveDemandStatus = isVirtualUserNewerThanDemand ? null : demandStatus;

  const isVirtualUserRunning = isVirtualUserNewerThanDemand && ACTIVE_STATUSES.includes(virtualUserStatus);
  const isVirtualUserFailed = isVirtualUserNewerThanDemand && virtualUserStatus === CREATE_REQUEST_STATUS.FAILED;

  const isDemandSuccessful = effectiveDemandStatus === CREATE_REQUEST_STATUS.COMPLETED;
  const isDemandFailed     = effectiveDemandStatus === CREATE_REQUEST_STATUS.FAILED || isVirtualUserFailed;
  const isDemandProcessing = effectiveDemandStatus === CREATE_REQUEST_STATUS.IN_PROGRESS || isVirtualUserRunning;
  const isDemandDraft      = effectiveDemandStatus
    ? Boolean(isDraftCaseData)
    : !(isVirtualUserRunning || isVirtualUserFailed);

  return {
    isDemandSuccessful,
    isDemandFailed,
    isDemandProcessing,
    isDemandDraft,
    isVirtualUserRunning,
    requestCreatedAt: (demandRequest ?? virtualUserRequest)?.createdAt
  };
};

export default useResultProgress;